// Ecommerce → Configuración: Tienda (/ecommerce/config), Métodos de Pago
// (/ecommerce/pagos) y Envíos (/ecommerce/envios). Solo administradores para
// tienda/pagos/smtp (las RPCs lo exigen). Credenciales cifradas write-only.
import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CreditCard, ExternalLink, Store, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Switch, fieldCls } from '@/features/admin/shared';
import { ecomRpc, usd } from './shared';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';

interface CfgT {
  tienda: {
    slug: string; nombre: string; activo: boolean; location_id: string | null;
    lista_precio_id: string | null; whatsapp: string | null; email: string | null;
    color_primario: string; buffer_stock: number; reserva_ttl_min: number;
    retiro_en_tienda: boolean; envio_gratis_desde: number | null; mensaje_transferencia: string | null;
  } | null;
  zonas: { id: string; nombre: string; tarifa: number; orden: number; activo: boolean }[];
  pagos: { metodo: string; modo: string; activo: boolean; credenciales_configuradas: boolean }[];
  smtp: { host: string | null; puerto: number; seguro: boolean; usuario: string | null;
    from_email: string | null; from_nombre: string | null; activo: boolean; password_configurada: boolean } | null;
  listas: { id: string; nombre: string }[];
}
interface LocationOpt { id: string; nombre: string }

const useCfg = () => useQuery<CfgT>({
  queryKey: ['ecom-config'], queryFn: () => ecomRpc<CfgT>('ecom_config_get', {}),
  staleTime: 0, refetchOnMount: 'always',
});

// ---------------------------------------------------------------------------
export function ConfigTienda() {
  const qc = useQueryClient();
  const { data } = useCfg();
  const { data: locations = [] } = useQuery<LocationOpt[]>({
    queryKey: ['ecom-locations'],
    queryFn: async () => (await (await import('@/lib/api/client')).apiClient
      .get('/rest/inventario.locations?select=id,nombre&deleted_at=is.null&order=nombre')).data,
  });
  const [f, setF] = useState({
    slug: '', nombre: '', activo: false, location_id: '', lista_precio_id: '',
    whatsapp: '', email: '', color_primario: '#0E7490', buffer_stock: '1', reserva_ttl_min: '45',
    retiro_en_tienda: true, envio_gratis_desde: '', mensaje_transferencia: '',
  });
  useEffect(() => {
    const t = data?.tienda;
    if (t) setF({
      slug: t.slug, nombre: t.nombre, activo: t.activo, location_id: t.location_id ?? '',
      lista_precio_id: t.lista_precio_id ?? '', whatsapp: t.whatsapp ?? '', email: t.email ?? '',
      color_primario: t.color_primario, buffer_stock: String(t.buffer_stock), reserva_ttl_min: String(t.reserva_ttl_min),
      retiro_en_tienda: t.retiro_en_tienda, envio_gratis_desde: t.envio_gratis_desde != null ? String(t.envio_gratis_desde) : '',
      mensaje_transferencia: t.mensaje_transferencia ?? '',
    });
  }, [data?.tienda]);
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));

  const guardar = async () => {
    try {
      await ecomRpc('ecom_tienda_guardar', { _data: {
        ...f, buffer_stock: Number(f.buffer_stock) || 0, reserva_ttl_min: Number(f.reserva_ttl_min) || 45,
        envio_gratis_desde: f.envio_gratis_desde || null, location_id: f.location_id || null,
        lista_precio_id: f.lista_precio_id || null,
      } });
      void qc.invalidateQueries({ queryKey: ['ecom-config'] });
      toast.success('Tienda guardada');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  return (
    <div className="p-6 space-y-4 max-w-3xl">
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><Store className="w-5 h-5" /> Configuración de la Tienda</h1>
        {f.slug && f.activo && (
          <a className="text-sm text-primary inline-flex items-center gap-1 hover:underline"
             href={`/tienda/${f.slug}`} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="w-3.5 h-3.5" /> /tienda/{f.slug}
          </a>
        )}
      </div>
      <Card>
        <CardContent className="pt-4 grid gap-3 md:grid-cols-2">
          <Campo label="Nombre de la tienda *">
            <input className={fieldCls} value={f.nombre} onChange={(e) => set('nombre', e.target.value)} />
          </Campo>
          <Campo label="Slug (URL: /tienda/slug) *">
            <input className={`${fieldCls} font-mono`} placeholder="mi-tienda" value={f.slug}
                   onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} />
          </Campo>
          <Campo label="Bodega que sirve la web *">
            <select className={fieldCls} value={f.location_id} onChange={(e) => set('location_id', e.target.value)}>
              <option value="">— Selecciona —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Lista de precios web (vacío = precio base)">
            <select className={fieldCls} value={f.lista_precio_id} onChange={(e) => set('lista_precio_id', e.target.value)}>
              <option value="">Precio base del producto</option>
              {(data?.listas ?? []).map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="WhatsApp (con código país)">
            <input className={fieldCls} placeholder="+5939…" value={f.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
          </Campo>
          <Campo label="Email de contacto">
            <input className={fieldCls} value={f.email} onChange={(e) => set('email', e.target.value)} />
          </Campo>
          <Campo label="Color principal">
            <input className={`${fieldCls} h-10`} type="color" value={f.color_primario} onChange={(e) => set('color_primario', e.target.value)} />
          </Campo>
          <Campo label="Buffer de stock (unidades que se reservan del público)">
            <input className={fieldCls} type="number" min="0" value={f.buffer_stock} onChange={(e) => set('buffer_stock', e.target.value)} />
          </Campo>
          <Campo label="Minutos de reserva por pago online (TTL)">
            <input className={fieldCls} type="number" min="10" value={f.reserva_ttl_min} onChange={(e) => set('reserva_ttl_min', e.target.value)} />
          </Campo>
          <Campo label="Envío gratis desde (vacío = nunca)">
            <input className={fieldCls} type="number" min="0" step="0.01" value={f.envio_gratis_desde}
                   onChange={(e) => set('envio_gratis_desde', e.target.value)} />
          </Campo>
          <Campo label="Datos bancarios (se muestran al pagar por transferencia)" className="md:col-span-2">
            <textarea className={fieldCls} rows={3} placeholder={'Banco Pichincha · Cta. Ahorros 22XXXX · A nombre de … · RUC …'}
                      value={f.mensaje_transferencia} onChange={(e) => set('mensaje_transferencia', e.target.value)} />
          </Campo>
          <div className="md:col-span-2 flex items-center justify-between border-t pt-3">
            <div className="flex items-center gap-6">
              <Switch checked={f.activo} onChange={(v) => set('activo', v)} label="Tienda ACTIVA (visible al público)" />
              <Switch checked={f.retiro_en_tienda} onChange={(v) => set('retiro_en_tienda', v)} label="Ofrece retiro en tienda" />
            </div>
            <Button onClick={() => void guardar()} disabled={!f.nombre.trim() || !f.slug.trim()}>Guardar tienda</Button>
          </div>
        </CardContent>
      </Card>

      <SmtpTienda data={data} />
    </div>
  );
}

function SmtpTienda({ data }: { data: CfgT | undefined }) {
  const qc = useQueryClient();
  const [f, setF] = useState({ host: '', puerto: '587', seguro: false, usuario: '', password: '', from_email: '', from_nombre: '', activo: false });
  useEffect(() => {
    const s = data?.smtp;
    if (s) setF({ host: s.host ?? '', puerto: String(s.puerto ?? 587), seguro: s.seguro, usuario: s.usuario ?? '',
      password: '', from_email: s.from_email ?? '', from_nombre: s.from_nombre ?? '', activo: s.activo });
  }, [data?.smtp]);
  const set = (k: keyof typeof f, v: string | boolean) => setF((x) => ({ ...x, [k]: v }));
  const guardar = async () => {
    try {
      await ecomRpc('ecom_smtp_set', { _data: { ...f, puerto: Number(f.puerto) || 587, password: f.password || null } });
      void qc.invalidateQueries({ queryKey: ['ecom-config'] });
      toast.success('Correo de la tienda guardado');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Correo de la tienda (SMTP propio del módulo)</CardTitle></CardHeader>
      <CardContent className="grid gap-3 md:grid-cols-3">
        <Campo label="Host SMTP"><input className={fieldCls} value={f.host} onChange={(e) => set('host', e.target.value)} /></Campo>
        <Campo label="Puerto"><input className={fieldCls} type="number" value={f.puerto} onChange={(e) => set('puerto', e.target.value)} /></Campo>
        <div className="flex items-end"><Switch checked={f.seguro} onChange={(v) => set('seguro', v)} label="SSL/TLS" /></div>
        <Campo label="Usuario"><input className={fieldCls} value={f.usuario} onChange={(e) => set('usuario', e.target.value)} /></Campo>
        <Campo label={`Contraseña ${data?.smtp?.password_configurada ? '(configurada; vacío = conservar)' : ''}`}>
          <input className={fieldCls} type="password" autoComplete="new-password" value={f.password} onChange={(e) => set('password', e.target.value)} />
        </Campo>
        <div />
        <Campo label="From (email)"><input className={fieldCls} value={f.from_email} onChange={(e) => set('from_email', e.target.value)} /></Campo>
        <Campo label="From (nombre)"><input className={fieldCls} value={f.from_nombre} onChange={(e) => set('from_nombre', e.target.value)} /></Campo>
        <div className="flex items-end justify-between">
          <Switch checked={f.activo} onChange={(v) => set('activo', v)} label="Activo" />
          <Button size="sm" onClick={() => void guardar()}>Guardar SMTP</Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
const METODOS = [
  { m: 'deuna', l: 'De Una (QR Banco Pichincha)', sim: true },
  { m: 'payphone', l: 'Payphone (tarjetas)', sim: true },
  { m: 'transferencia', l: 'Transferencia bancaria', sim: false },
  { m: 'contraentrega', l: 'Pago contra entrega', sim: false },
];
export function PagosWeb() {
  const qc = useQueryClient();
  const { data } = useCfg();
  const set = async (metodo: string, d: Record<string, unknown>) => {
    try {
      await ecomRpc('ecom_pago_set', { _metodo: metodo, _data: d });
      void qc.invalidateQueries({ queryKey: ['ecom-config'] });
      toast.success('Método actualizado');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const cfg = (m: string) => (data?.pagos ?? []).find((x) => x.metodo === m);
  const dlg = useDialogoEnLinea();
  return (
    <div className="p-6 space-y-4 max-w-3xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><CreditCard className="w-5 h-5" /> Métodos de Pago</h1>
      <Card>
        <CardContent className="pt-4 space-y-2">
          {METODOS.map((x) => {
            const c = cfg(x.m);
            return (
              <div key={x.m} className="border rounded-md px-3 py-2.5 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-48">
                  <div className="font-medium">{x.l}</div>
                  {x.sim && (
                    <div className="text-xs text-muted-foreground">
                      {c?.modo === 'directo' ? 'Modo directo (requiere credenciales)' : 'Modo simulado (pruebas: paga solo a los ~10s)'}
                      {c?.credenciales_configuradas ? ' · credenciales ✓' : ''}
                    </div>
                  )}
                  {!x.sim && x.m === 'transferencia' && <div className="text-xs text-muted-foreground">El cliente sube el comprobante; tú lo apruebas en Pedidos.</div>}
                  {!x.sim && x.m === 'contraentrega' && <div className="text-xs text-muted-foreground">Se factura al despachar (la factura viaja con el paquete).</div>}
                </div>
                {x.sim && (
                  <select className={`${fieldCls} !w-auto`} value={c?.modo ?? 'simulado'}
                          onChange={(e) => void set(x.m, { modo: e.target.value })}>
                    <option value="simulado">Simulado</option>
                    <option value="directo">Directo</option>
                  </select>
                )}
                {x.sim && c?.modo === 'directo' && (
                  <Button size="sm" variant="outline" onClick={async () => {
                    const v = await dlg.pedir(`Credenciales de ${x.l}`, [
                      { key: 'cred', label: 'JSON o token (se guardan cifradas)', multilinea: true },
                    ]);
                    if (v?.cred) void set(x.m, { credenciales: v.cred });
                  }}>Credenciales</Button>
                )}
                <Switch checked={c?.activo ?? false} label="" onChange={(v) => void set(x.m, { activo: v })} />
              </div>
            );
          })}
          <p className="text-xs text-muted-foreground border-t pt-2">
            De Una y Payphone traen modo SIMULADO para probar el flujo completo sin credenciales; el modo directo
            (webhook real) se activa cuando tengas la afiliación. Activa al menos un método para poder vender.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
export function EnviosWeb() {
  const qc = useQueryClient();
  const { data } = useCfg();
  const [f, setF] = useState({ nombre: '', tarifa: '' });
  const guardar = async (d: Record<string, unknown>, id: string | null = null, eliminar = false) => {
    try {
      await ecomRpc('ecom_zona_guardar', { _data: d, _id: id, _eliminar: eliminar });
      void qc.invalidateQueries({ queryKey: ['ecom-config'] });
      if (!eliminar && !id) { setF({ nombre: '', tarifa: '' }); }
      toast.success(eliminar ? 'Zona eliminada' : 'Zona guardada');
    } catch (e) { toast.error(getErrorMessage(e)); }
  };
  const dlg = useDialogoEnLinea();
  return (
    <div className="p-6 space-y-4 max-w-3xl">
      {dlg.ui}
      <h1 className="text-xl font-semibold flex items-center gap-2"><Truck className="w-5 h-5" /> Zonas y tarifas de envío</h1>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            Zonas {data?.tienda?.envio_gratis_desde != null && `· envío gratis desde ${usd(data.tienda.envio_gratis_desde)}`}
            {data?.tienda?.retiro_en_tienda && ' · retiro en tienda activo'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data?.zonas ?? []).map((z) => (
            <div key={z.id} className="border rounded-md px-3 py-2 flex items-center gap-3 text-sm">
              <span className="flex-1 font-medium">{z.nombre}</span>
              <span className="font-mono">{Number(z.tarifa) === 0 ? 'Gratis' : usd(z.tarifa)}</span>
              <Switch checked={z.activo} label="" onChange={(v) => void guardar({ activo: v }, z.id)} />
              <Button size="sm" variant="ghost" onClick={async () => {
                const v = await dlg.pedir(`Nueva tarifa para "${z.nombre}"`, [
                  { key: 'tarifa', label: 'Tarifa', tipo: 'number', valor: String(z.tarifa) },
                ]);
                if (v) void guardar({ tarifa: Number(v.tarifa) || 0 }, z.id);
              }}>✎</Button>
              <Button size="sm" variant="ghost" onClick={async () => {
                if (await dlg.confirmar(`¿Eliminar la zona "${z.nombre}"?`, { peligro: true })) void guardar({}, z.id, true);
              }}>🗑</Button>
            </div>
          ))}
          {!data?.zonas?.length && <p className="text-sm text-muted-foreground">Sin zonas. Sugerencia: "Ciudad (repartidor propio)", "Quito/Guayaquil/Cuenca", "Resto del país".</p>}
          <div className="border rounded-md p-3 flex gap-2 items-end">
            <Campo label="Nueva zona">
              <input className={`${fieldCls} !w-64`} placeholder="Resto del país" value={f.nombre} onChange={(e) => setF({ ...f, nombre: e.target.value })} />
            </Campo>
            <Campo label="Tarifa">
              <input className={`${fieldCls} !w-28`} type="number" min="0" step="0.01" placeholder="0.00" value={f.tarifa} onChange={(e) => setF({ ...f, tarifa: e.target.value })} />
            </Campo>
            <Button size="sm" disabled={!f.nombre.trim()} onClick={() => void guardar({ nombre: f.nombre.trim(), tarifa: Number(f.tarifa) || 0 })}>Agregar</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
